/**
 * A mentor's own profile, and their password.
 *
 * *** THIS SCREEN'S REAL JOB IS VISIBILITY ***
 * A mentor whose profile is incomplete does not appear to students at all —
 * `is_visible` is false and they get no bookings, with nothing anywhere
 * saying why. The server computes `missing_fields` for exactly this, so the
 * incomplete state leads the page rather than hiding under a form.
 */

import { useEffect, useState } from 'react'
import { Card } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import {
  useChangeMyPassword,
  useMyMentorProfile,
  useUpdateMyMentorProfile,
} from '@/lib/queries'
import styles from './ProfilePage.module.css'

/** `missing_fields` carries API names; these are what a person calls them. */
const FIELD_LABELS: Record<string, string> = {
  title: 'a headline',
  bio: 'an introduction',
  photo_url: 'a photo',
  expertise_tags: 'your subjects',
  full_name: 'your name',
}

function describeMissing(fields: string[]): string {
  const named = fields.map((f) => FIELD_LABELS[f] ?? f)
  if (named.length === 1) return named[0] ?? ''
  if (named.length === 2) return `${named[0]} and ${named[1]}`
  return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`
}

export function MentorProfile() {
  const toast = useToast()
  const profile = useMyMentorProfile()
  const update = useUpdateMyMentorProfile()
  const changePassword = useChangeMyPassword()

  const [title, setTitle] = useState('')
  const [bio, setBio] = useState('')
  const [tags, setTags] = useState('')

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')

  const loaded = profile.data
  useEffect(() => {
    if (!loaded) return
    setTitle(loaded.title ?? '')
    setBio(loaded.bio ?? '')
    setTags(loaded.expertise_tags.join(', '))
    // Seeded once per identity, so a background refetch cannot wipe a
    // half-written bio.
  }, [loaded?.user_id])

  if (profile.isLoading) {
    return <p className={styles.muted}>Loading your profile…</p>
  }

  if (profile.error) {
    return (
      <p className={styles.error}>
        {profile.error instanceof ApiError
          ? profile.error.message
          : 'Could not load your profile.'}
      </p>
    )
  }

  const p = profile.data
  if (!p) return null

  const parsedTags = tags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)

  const changed =
    title.trim() !== (p.title ?? '') ||
    bio.trim() !== (p.bio ?? '') ||
    parsedTags.join(',') !== p.expertise_tags.join(',')

  const save = async () => {
    try {
      await update.mutateAsync({
        /*
          Only what changed. `title` has a 2-char minimum and `bio` a
          10-char one server-side, so sending an empty string would be a
          validation error rather than a clear.
        */
        ...(title.trim() !== (p.title ?? '') ? { title: title.trim() } : {}),
        ...(bio.trim() !== (p.bio ?? '') ? { bio: bio.trim() } : {}),
        ...(parsedTags.join(',') !== p.expertise_tags.join(',')
          ? { expertise_tags: parsedTags }
          : {}),
      })
      toast.success('Profile saved.')
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save that.',
      )
    }
  }

  const submitPassword = async () => {
    try {
      await changePassword.mutateAsync({
        current_password: current,
        new_password: next,
      })
      toast.success('Password changed.')
      setCurrent('')
      setNext('')
      // `must_change_password` is cleared server-side.
      await profile.refetch()
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not change your password.',
      )
    }
  }

  return (
    <>
      {/*
        *** THE MOST IMPORTANT THING ON THE PAGE ***
        An invisible mentor receives no bookings and is given no reason. The
        server already names the fields; this says what they mean.
      */}
      {!p.is_visible && (
        <div className={styles.blocked}>
          <strong className={styles.blockedTitle}>
            Students cannot see you yet
          </strong>
          <p className={styles.blockedText}>
            {p.missing_fields.length > 0
              ? `Add ${describeMissing(p.missing_fields)} below and you will appear in the mentor list.`
              : 'Your institute has not made your profile visible yet.'}
          </p>
        </div>
      )}

      {p.must_change_password && (
        <div className={styles.blocked}>
          <strong className={styles.blockedTitle}>
            Change your password
          </strong>
          <p className={styles.blockedText}>
            Your account was created with a temporary password. Set your own
            below.
          </p>
        </div>
      )}

      <Card>
        <div className={styles.field}>
          <span className={styles.labelRow}>
            <span className={styles.label}>Name</span>
            {p.rating_count > 0 && p.rating_avg && (
              <Pill tone="neutral" subtle>
                ★ {p.rating_avg} ({p.rating_count})
              </Pill>
            )}
          </span>
          <p className={styles.readOnly}>{p.full_name}</p>
          <p className={styles.hint}>
            Your institute set this. Ask them to change it.
          </p>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="mentor-title">
            Headline
          </label>
          <input
            id="mentor-title"
            className={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Mathematics, Quant, Reasoning"
            maxLength={200}
          />
          <p className={styles.hint}>
            One line under your name in the mentor list.
          </p>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="mentor-bio">
            Introduction
          </label>
          <textarea
            id="mentor-bio"
            className={styles.textarea}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="What you teach, and how you work with students."
            maxLength={2000}
            rows={4}
          />
          <p className={styles.hint}>
            At least 10 characters. This is what helps a student choose you.
          </p>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="mentor-tags">
            Subjects
          </label>
          <input
            id="mentor-tags"
            className={styles.input}
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Algebra, Calculus, Reasoning"
          />
          <p className={styles.hint}>
            Separated by commas. Up to ten, 40 characters each.
          </p>
        </div>

        <div className={styles.actions}>
          <Button
            variant="primary"
            onClick={save}
            disabled={!changed}
            loading={update.isPending}
          >
            Save profile
          </Button>
        </div>
      </Card>

      <Card>
        <div className={styles.field}>
          <span className={styles.label}>Email</span>
          <p className={styles.readOnly}>{p.email}</p>
          <p className={styles.hint}>You sign in with this address.</p>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="pw-current">
            Current password
          </label>
          <input
            id="pw-current"
            type="password"
            className={styles.input}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="pw-new">
            New password
          </label>
          <input
            id="pw-new"
            type="password"
            className={styles.input}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
          />
        </div>

        <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={submitPassword}
            /*
              The server owns the real rules — length, complexity, and
              refusing the current password. This only stops an obviously
              empty submission.
            */
            disabled={current.length === 0 || next.length === 0}
            loading={changePassword.isPending}
          >
            Change password
          </Button>
        </div>
      </Card>
    </>
  )
}
